import { faroCoreApiClient } from "@api/faro-core-api-client";
import { InviteMemberButton } from "@components/common/invite-member-button";
import { GroupAutoComplete } from "@components/common/group-autocomplete/group-autocomplete";
import { LabelWithHelp } from "@components/common/label-with-help";
import { EnableGroupCreationCheckbox } from "@components/groups/enable-group-creation-checkbox";
import { CompanyRoleAutoComplete } from "@components/role/role-autocomplete/company-role-autocomplete";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { CompanyId, GroupId } from "@custom-types/base-types";
import { useRespondToInvite } from "@hooks/use-respond-to-invite";
import { Stack } from "@mui/material";
import { useCompanyId } from "@context-providers/company-id-provider";
import { INVITE_DIALOG_CLOSE_DELAY_MS } from "@src/constants/invite-constants";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { setMany } from "@store/members/members-slice";
import { useAppDispatch } from "@store/store-helper";
import { delay } from "@utils/time-utils";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { createGroupsPermission } from "@utils/user-utils";
import { useState } from "react";
import { Dialog } from "@flat-ui";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { MembersAutocomplete } from "@components/common/members-autocomplete/members-autocomplete";
import { useMembersAutocomplete } from "@hooks/autocomplete/use-members-autocomplete";
import { useCompanyMembers } from "@hooks/use-company-members";
import { shouldDisableSendInvite } from "@utils/members-invite-utils";

interface Props {
  /**
   * Determines the variant of the invite member button
   *  - "outlined" (default): Contained button with white background and blue border
   *  - "filled": Normal button with blue background
   */
  buttonVariant?: "outlined" | "filled";
}

/** Renders the invite member button and the functionality in member page */
export function InviteMemberToCompany({
  buttonVariant = "outlined",
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const { handleErrorWithToast } = useErrorContext();
  const { respondToUser } = useRespondToInvite();
  const { trackEvent } = useTrackEvent();
  const companyMembers = useCompanyMembers();

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // TODO: https://faro01.atlassian.net/browse/ST-646
  const defaultRole =
    "MEMBER" as FaroCoreApiTypes.AssignmentRoleBySubjectType["company"];
  const [selectedRole, setSelectedRole] =
    useState<FaroCoreApiTypes.AssignmentRoleBySubjectType["company"]>(
      defaultRole
    );
  const [selectedGroupId, setSelectedGroupId] = useState<GroupId | undefined>(
    undefined
  );
  const [shouldCreateGroups, setShouldCreateGroups] = useState<boolean>(false);

  const {
    selectedMembers,
    uniqueSelectedValues,
    helperText,
    isMemberInputValid,
    handleMemberSelect,
    onInputChange,
    isNewEmailValid,
    reset,
  } = useMembersAutocomplete({
    entityName: "company",
    existingMembers: companyMembers,
    options: [],
    companyMemberIds: companyMembers.map((member) => member.identity),
    invalidInputMessage: "Enter a valid email address",
  });

  // Selected roles that needs to show group list when inviting them
  const shouldShowGroupList =
    selectedRole === "COMPANY_MANAGER" ||
    selectedRole === "GROUP_VIEWER" ||
    selectedRole === "PROJECT_MANAGER";

  const shouldDisableConfirm =
    shouldDisableSendInvite(uniqueSelectedValues, isMemberInputValid) ||
    (shouldShowGroupList && !selectedGroupId);

  function onOpenInviteDialog(): void {
    reset();
    setIsDialogOpen(true);
    setSelectedRole(
      "MEMBER" as FaroCoreApiTypes.AssignmentRoleBySubjectType["company"]
    );
    setSelectedGroupId(undefined);
  }

  /** This function creates payload and call the appropriate route */
  async function createPayloadAndSendInvite({
    shouldAddMemberToGroup,
    companyId,
  }: {
    shouldAddMemberToGroup: boolean;
    companyId: CompanyId;
  }): Promise<void> {
    // Additional permission to be added if user should be able to create groups
    const permission: FaroCoreApiTypes.UserPermission | undefined =
      shouldCreateGroups && selectedRole === "COMPANY_MANAGER"
        ? createGroupsPermission
        : undefined;

    if (shouldAddMemberToGroup && selectedGroupId) {
      const payload: FaroCoreApiTypes.BulkMemberInvitePayload<"group"> = {
        assignments: [
          {
            identities: uniqueSelectedValues,
            role: selectedRole,
            permission,
          },
        ],
      };

      const response: FaroCoreApiTypes.AddMembersToGroupResponse =
        await faroCoreApiClient.groups.addMembersToGroup({
          companyId,
          groupId: selectedGroupId,
          payload,
        });

      respond(response);
    } else {
      const payload: FaroCoreApiTypes.BulkMemberInvitePayload<"company"> = {
        assignments: [
          {
            identities: uniqueSelectedValues,
            role: selectedRole,
            permission,
          },
        ],
      };

      const response: FaroCoreApiTypes.AddMembersToCompanyResponse =
        await faroCoreApiClient.company.addMembersToCompany({
          companyId,
          payload,
        });

      respond(response);
    }
  }

  /** This function checks the backend response and show related toast message based on it */
  function respond(
    response:
      | FaroCoreApiTypes.AddMembersToCompanyResponse
      | FaroCoreApiTypes.AddMembersToGroupResponse
  ): void {
    // Add invited member to the store. response.data.members exist when the response is either success or warning
    if (response.data.members) {
      dispatch(
        setMany(
          response.data.members.filter((member) => member.kind === "user")
        )
      );
    }

    respondToUser(response, uniqueSelectedValues.length);
  }

  /** Close the dialog and reset loading state */
  async function closeDialog(): Promise<void> {
    setIsDialogOpen(false);
    // Delay to ensure dialog closes before resetting loading state
    // This prevents duplicate invitation requests
    await delay(INVITE_DIALOG_CLOSE_DELAY_MS);
    setIsLoading(false);
  }

  /** Submit members to be invited */
  async function handleConfirm(): Promise<void> {
    setIsLoading(true);

    trackEvent({
      name: WorkspaceEvents.inviteMembers,
      props: {
        numberOfMembers: uniqueSelectedValues.length,
        role: selectedRole,
      },
    });

    try {
      await createPayloadAndSendInvite({
        shouldAddMemberToGroup: !!selectedGroupId,
        companyId,
      });

      void closeDialog();
    } catch (error) {
      setIsLoading(false);

      handleErrorWithToast({
        id: `inviteMember-${Date.now().toString()}`,
        title: "Cannot invite member(s)",
        error,
      });
    }
  }

  return (
    <>
      <InviteMemberButton
        buttonVariant={buttonVariant}
        onClick={onOpenInviteDialog}
      />

      <Dialog
        title="Invite members to workspace"
        confirmText="Send Invite"
        open={isDialogOpen}
        onConfirm={handleConfirm}
        showSpinner={isLoading}
        isConfirmDisabled={shouldDisableConfirm}
        onCancel={closeDialog}
        dataTestId="invite-members-to-workspace-dialog"
      >
        <Stack marginBottom={SPACE_ELEMENTS_OF_MODAL}>
          <CompanyRoleAutoComplete
            selectedRole={selectedRole}
            shouldAllowAllRolesSelection={true}
            onChange={(role) => role && setSelectedRole(role)}
            onResetSelection={() => setSelectedRole(defaultRole)}
            isRequired
          />
        </Stack>
        {shouldShowGroupList && (
          <Stack marginBottom={SPACE_ELEMENTS_OF_MODAL}>
            <LabelWithHelp title="Group" isRequired />
            <GroupAutoComplete
              callbackOnGroupSelected={setSelectedGroupId}
              selectedGroupId={selectedGroupId}
              placeholder={"Please select a group..."}
            />
          </Stack>
        )}
        <MembersAutocomplete
          isRequired
          hasAutoFocus
          options={[]}
          label="Emails"
          helperText={helperText}
          selectedOptions={selectedMembers}
          isValid={isNewEmailValid}
          onInputChange={onInputChange}
          handleMemberSelect={handleMemberSelect}
          placeHolder="Enter email addresses (Separated by comma, semicolon, or space)"
        />

        <EnableGroupCreationCheckbox
          selectedRole={selectedRole}
          shouldCreateGroups={shouldCreateGroups}
          membersCount={selectedMembers.length}
          setShouldCreateGroups={setShouldCreateGroups}
        />
      </Dialog>
    </>
  );
}
